import { useUpdateVetterEntityMutation } from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

/**
 * The supplier's own name and description. Only these (and the primary
 * contact, set from the people list) are the supplier's to change — active
 * status and notes are the studio's, and the backend refuses them.
 */
export function EditCompanyModal({
	open,
	onOpenChange,
	company,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	company: { id: string; name: string; description?: string | null };
	onSuccess: () => void;
}) {
	const [name, setName] = useState(company.name);
	const [description, setDescription] = useState(company.description ?? '');
	const [error, setError] = useState<string | null>(null);
	const [update, { loading }] = useUpdateVetterEntityMutation();

	// Refill from the latest values each time it opens.
	const [wasOpen, setWasOpen] = useState(false);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) {
			setName(company.name);
			setDescription(company.description ?? '');
			setError(null);
		}
	}

	const handleSubmit = async () => {
		if (!name.trim() || loading) return;
		setError(null);
		try {
			const { data } = await update({
				variables: {
					id: company.id,
					input: { name: name.trim(), description: description.trim() || null },
				},
			});
			const res = data?.updateVetterEntity;
			if (!res?.success) {
				setError(res?.message || 'Could not save your company details.');
				return;
			}
			toast.success('Company details saved');
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not save your company details.');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Company details</DialogTitle>
					<DialogDescription>How your company appears to 05 Creation.</DialogDescription>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label>
									Name <span className="text-destructive">*</span>
								</Label>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									disabled={loading}
								/>
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>Description</Label>
								<Textarea
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									rows={4}
									placeholder="What you make, where, and anything the studio should know."
									disabled={loading}
								/>
							</div>
							{error && <div className="text-sm text-status-danger">{error}</div>}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || loading}>
							{loading ? 'Saving…' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
